import { cn } from '@v2/shared/lib/utils';

type Props = {
  count: number;
  withProductStrip?: boolean;
};

const BAR = 'rounded bg-slate-200';

/**
 * What a card looks like before it arrives.
 *
 * Sized to the real card so that the grid does not resize when the reviews land — the
 * page this replaces jumped every time the widget finished, and not reintroducing that
 * is the reason these are shaped rather than a single spinner in an empty box.
 *
 * Only ever rendered in the browser: the server draws real reviews or nothing at all,
 * so a skeleton can never end up in the HTML a crawler reads.
 */
export default function ReviewSkeleton({ count, withProductStrip = false }: Props) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <article
          key={index}
          aria-hidden
          className="animate-pulse overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm"
        >
          {withProductStrip && (
            <div className="flex items-center gap-3.5 border-0 border-b border-solid border-slate-200 bg-slate-50 px-5 py-3">
              <span className={cn(BAR, 'h-[76px] w-14 shrink-0')} />
              <span className="grid gap-1.5">
                <span className={cn(BAR, 'block h-3.5 w-32')} />
                <span className={cn(BAR, 'block h-2.5 w-20')} />
              </span>
            </div>
          )}

          <div className="p-6">
            <div className="mb-3 flex items-center gap-3">
              <span className={cn(BAR, 'h-10 w-10 shrink-0 rounded-full')} />
              <span className="grid gap-1.5">
                <span className={cn(BAR, 'block h-3.5 w-28')} />
                <span className={cn(BAR, 'block h-2.5 w-20')} />
              </span>
            </div>

            <span className={cn(BAR, 'mb-2.5 block h-3 w-24')} />
            <span className={cn(BAR, 'mb-2 block h-4 w-2/3')} />
            <span className={cn(BAR, 'mb-1.5 block h-3 w-full')} />
            <span className={cn(BAR, 'block h-3 w-4/5')} />
          </div>
        </article>
      ))}
    </>
  );
}
